/**
 * Browser notification utilities for chat sessions
 */
export interface NotificationOptions {
  title: string;
  body?: string;
  icon?: string;
  tag?: string;
  requireInteraction?: boolean;
  onClick?: () => void;
}

/**
 * Check if browser notifications are supported
 */
export const isNotificationSupported = (): boolean => {
  return "Notification" in globalThis;
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = (): NotificationPermission => {
  if (!isNotificationSupported()) {
    return "denied";
  }
  return Notification.permission;
};

/**
 * Request notification permission from the user
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!isNotificationSupported()) {
    return "denied";
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (_error) {
    return "denied";
  }
};

/**
 * Send a browser notification
 * Automatically requests permission if not already granted
 */
export const sendNotification = async (options: NotificationOptions): Promise<Notification | null> => {
  if (!isNotificationSupported()) {
    return null;
  }

  // Request permission if not already granted
  let permission = getNotificationPermission();
  if (permission === "default") {
    permission = await requestNotificationPermission();
  }

  if (permission !== "granted") {
    return null;
  }

  try {
    const notification = new Notification(options.title, {
      body: options.body,
      icon: options.icon,
      tag: options.tag,
      requireInteraction: options.requireInteraction,
    });

    // Handle click event if provided
    if (options.onClick) {
      notification.onclick = () => {
        options.onClick?.();
        notification.close();
      };
    }

    return notification;
  } catch (_error) {
    return null;
  }
};

/**
 * Check if the document/tab is currently visible to the user
 */
export const isDocumentVisible = (): boolean => {
  return document.visibilityState === "visible";
};

/**
 * Check if the window has focus
 */
export const isWindowFocused = (): boolean => {
  return document.hasFocus();
};

/**
 * Check if the window/tab is considered "active" (both visible and focused)
 */
export const isWindowActive = (): boolean => {
  return isDocumentVisible() && isWindowFocused();
};
